import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { blogPosts } from '../data/blogData';
import ReactMarkdown from 'react-markdown';

export const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);
  const currentIndex = blogPosts.findIndex(p => p.slug === slug);
  const nextPost = currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null;
  const prevPost = currentIndex > 0 ? blogPosts[currentIndex - 1] : null;

  useEffect(() => {
    window.scrollTo(0, 0);
    if (post) {
      document.title = `${post.title} | Precision Mobile Detailing Blog`;
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', post.excerpt);
      }
    }
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Post Not Found</h1>
          <Link to="/blog" className="text-black hover:underline">
            Return to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Image */}
      <div className="relative h-96 bg-gray-900">
        <img 
          src={post.image} 
          alt={post.title}
          className="w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center text-white">
              <Link to="/blog" className="inline-flex items-center gap-2 text-white hover:underline mb-6">
                <ArrowLeft className="w-4 h-4" />
                Back to Blog
              </Link>
              <div className="flex items-center justify-center gap-4 text-sm mb-4">
                <span className="bg-white text-black px-3 py-1 rounded-full font-semibold">
                  {post.category}
                </span>
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>{post.date}</span>
                </div>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold">
                {post.title}
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <article className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="prose prose-lg max-w-none">
              <ReactMarkdown
                components={{
                  h2: ({node, ...props}) => <h2 className="text-3xl font-bold mt-12 mb-6" {...props} />,
                  h3: ({node, ...props}) => <h3 className="text-2xl font-bold mt-8 mb-4" {...props} />,
                  p: ({node, ...props}) => <p className="text-gray-700 leading-relaxed mb-6" {...props} />,
                  ul: ({node, ...props}) => <ul className="list-disc pl-6 mb-6 space-y-2" {...props} />,
                  ol: ({node, ...props}) => <ol className="list-decimal pl-6 mb-6 space-y-2" {...props} />,
                  li: ({node, ...props}) => <li className="text-gray-700" {...props} />,
                  strong: ({node, ...props}) => <strong className="font-bold text-gray-900" {...props} />,
                  a: ({node, ...props}) => <Link className="text-black font-semibold hover:underline" {...props} />,
                  blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-black pl-6 italic my-6" {...props} />
                }}
              >
                {post.content}
              </ReactMarkdown>
            </div>

            {/* Call to Action */}
            <div className="mt-16 p-8 bg-gray-50 rounded-lg border-2 border-gray-200">
              <h3 className="text-2xl font-bold mb-4">Ready for Professional Mobile Detailing?</h3>
              <p className="text-gray-600 mb-6">
                Book your appointment today and experience premium car care at your location in the Inland Empire.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="cta-button">
                  <Link to="/booking">
                    Book Now <ArrowRight className="ml-2" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/services">
                    View Services
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Previous/Next Navigation */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {prevPost && (
                <Link to={`/blog/${prevPost.slug}`} className="group">
                  <div className="border-2 border-gray-200 rounded-lg p-6 hover:border-black transition-all">
                    <p className="text-sm text-gray-600 mb-2 flex items-center gap-2">
                      <ArrowLeft className="w-4 h-4" />
                      Previous Article
                    </p>
                    <h4 className="text-xl font-bold group-hover:text-gray-700 transition-colors">
                      {prevPost.title}
                    </h4>
                  </div>
                </Link>
              )}
              {nextPost && (
                <Link to={`/blog/${nextPost.slug}`} className="group md:ml-auto">
                  <div className="border-2 border-gray-200 rounded-lg p-6 hover:border-black transition-all text-right">
                    <p className="text-sm text-gray-600 mb-2 flex items-center justify-end gap-2">
                      Next Article
                      <ArrowRight className="w-4 h-4" />
                    </p>
                    <h4 className="text-xl font-bold group-hover:text-gray-700 transition-colors">
                      {nextPost.title}
                    </h4>
                  </div>
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
